/* eslint-disable react/require-default-props */

import React from 'react';
import {
  Box,
  Button,
  FooterActionBar as FooterActionBarCore,
  Icon,
  Tooltip,
} from '@saviynt/design-system';
import cx from 'classnames';
import { useSelector } from 'react-redux';

import { type RootState } from '../../../services/types';

import './FooterActionBar.css';

interface FooterActionBarProps {
  /** Callback when back button is clicked */
  onBack?: () => void;
  /** Callback when activate rule button is clicked */
  onActivateRule?: () => void;
  /** Whether the activate button is loading */
  isActivating?: boolean;
  /** Whether the activate button is disabled */
  isActivateButtonDisabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Footer action bar component containing Back and Activate Rule buttons
 *
 * @param props - The component props
 * @returns JSX element containing the footer action bar
 */
function FooterActionBar({
  onBack,
  onActivateRule,
  isActivating = false,
  isActivateButtonDisabled = false,
  className,
}: FooterActionBarProps) {
  // Get menu pinned state from Redux store
  const isMenuPinned = useSelector(
    (state: RootState) => state.user.isMenuPinned
  );

  return (
    <FooterActionBarCore
      className={cx(
        'Rules-FooterActionBar',
        className,
        isMenuPinned && 'Rules-FooterActionBar--isMenuPinned'
      )}
      size='large'>
      {/* Back button */}
      <Box className='Rules-FooterActionBar-left'>
        <Button
          kind='outlined'
          onClick={onBack}
          className='Rules-FooterActionBar-backButton'>
          Back
        </Button>
      </Box>

      {/* Activate Rule button */}
      <Box className='Rules-FooterActionBar-right'>
        <Tooltip
          text={
            isActivateButtonDisabled
              ? "You don't have the permissions to activate or the feature is turned off."
              : ''
          }
          isDisabled={!isActivateButtonDisabled}
          placement='top'>
          <Button
            kind='filled'
            onClick={onActivateRule}
            isLoading={isActivating}
            isDisabled={isActivateButtonDisabled}
            loaderText='Activating...'
            leftIcon={
              <Icon
                kind='QuickAction'
                size='medium'
                color='--color-foreground-neutral-inverse'
              />
            }
            className='Rules-FooterActionBar-activateButton'>
            Activate Rule
          </Button>
        </Tooltip>
      </Box>
    </FooterActionBarCore>
  );
}

export default FooterActionBar;
